import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import api from '../api';
import { Badge, StatCard, Skeleton } from '../ui';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { FaUsers, FaCircleCheck, FaBoxOpen, FaListCheck, FaHelmetSafety, FaSackDollar, FaClipboardCheck, FaFire } from 'react-icons/fa6';

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'শুভ সকাল' : h < 17 ? 'শুভ বিকাল' : 'শুভ সন্ধ্যা';
};

const COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#06B6D4', '#F43F5E'];

export default function Dashboard() {
  const { profile } = useAuth();
  const role = profile?.user?.role;
  if (role === 'owner') return <OwnerDash />;
  if (role === 'manager') return <ManagerDash />;
  if (role === 'contractor') return <ContractorDash />;
  if (role === 'worker') return <WorkerDash />;
  return null;
}

function Loading() {
  return <div className="space-y-4"><div className="grid grid-cols-2 lg:grid-cols-4 gap-3"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div><Skeleton className="h-72" /></div>;
}

function SectionTitle({ title, sub }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-bold text-ink-900">{title}</h2>
      {sub && <p className="text-xs text-ink-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Owner ────────────────────────────────────────────────────────
function OwnerDash() {
  const { profile } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/owner').then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <Loading />;
  const { project, stats, low_stock, recent_activity, charts } = data;

  const weeklyData = (charts?.weekly || []).map((r) => ({
    day: (r.day || '').slice(5),
    In: Number(r.stock_in || 0),
    Out: Number(r.stock_out || 0),
  }));
  const contractorPie = (charts?.contractor_stock || []).map((r) => ({ name: r.contractor_name, value: Number(r.qty || 0) }));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-lg"><FaHelmetSafety /></div>
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink-900">{greeting()}, {profile?.user?.name} 👋</h1>
          <p className="text-sm text-ink-500">{project?.name} — ৭ দিনের চালচিত্র</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<FaUsers />} tone="sky" label="কর্মী" value={stats.workers} sub={`${stats.contractors} কন্ট্রাক্টর`} />
        <StatCard icon={<FaCircleCheck />} tone="emerald" label="আজ উপস্থিত" value={stats.attendance?.present ?? 0} sub={`মোট ${stats.attendance?.total ?? 0} জনের`} />
        <StatCard icon={<FaBoxOpen />} tone="amber" label="আজ মাল বেরিয়েছে" value={stats.stock_today} />
        <StatCard icon={<FaListCheck />} tone="rose" label="পেন্ডিং অনুরোধ" value={stats.pending_requests} />
      </div>

      {/* Stock trend */}
      <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
        <SectionTitle title="সাপ্তাহিক মাল প্রবাহ" sub="গত ৭ দিনের স্টক ইন/আউট" />
        <div className="flex items-center gap-4 mb-3 text-xs text-ink-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-brand-500" /> মাল ঢুকেছে</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-ink-200" /> মাল বেরিয়েছে</span>
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={weeklyData}>
              <defs>
                <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={32} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)' }} />
              <Area type="monotone" dataKey="In" stroke="#F59E0B" fill="url(#inGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="Out" stroke="#CBD5E1" fill="none" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Today's activity */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <SectionTitle title="আজকের মাল বিতরণ" sub="কে কখন কী নিলো" />
          <div className="space-y-3">
            {recent_activity?.slice(0, 6).map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
                <span className="text-ink-700 font-medium">{a.material_name}</span>
                <span className="text-ink-300">→</span>
                <span className="text-ink-500 truncate">{a.contractor_name || 'সাধারণ'}</span>
              </div>
            ))}
            {(!recent_activity || recent_activity.length === 0) && <div className="text-sm text-ink-400 py-6 text-center">আজ এখনো কোনো মাল বিতরণ হয়নি</div>}
          </div>
        </div>

        {/* Contractor stock pie */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <SectionTitle title="সাইড অনুযায়ী মাল" sub="কে কতটুকু মাল নিয়েছে" />
          {contractorPie.length > 0 ? (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-44 w-full max-w-[180px] mx-auto">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={contractorPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                      {contractorPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 min-w-[140px] space-y-1.5">
                {contractorPie.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-ink-600 truncate"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</span>
                    <span className="font-semibold text-ink-800">{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-sm text-ink-400 py-8 text-center">এখনো ডেটা নেই</div>}
        </div>
      </div>

      {/* Low stock alert */}
      {low_stock?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <FaFire className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-800 text-sm">মাল কম — দ্রুত স্টক যোগ করুন</h3>
            <div className="mt-1.5 space-y-1 text-sm text-amber-700">
              {low_stock.slice(0, 5).map((m, i) => (
                <div key={i} className="flex justify-between gap-6"><span>{m.name}</span><span className="font-semibold">{m.balance} {m.unit}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Manager ──────────────────────────────────────────────────────
function ManagerDash() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/manager/dashboard').then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loading />;
  const { stats, today_issues, attendance_by_contractor } = s;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-lg"><FaHelmetSafety /></div>
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink-900">{greeting()}, ম্যানেজার 👷</h1>
          <p className="text-sm text-ink-500">আজকের সাইটের সারসংক্ষেপ</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<FaUsers />} tone="sky" label="কর্মী" value={stats.workers} />
        <StatCard icon={<FaCircleCheck />} tone="emerald" label="আজ উপস্থিত" value={stats.present} sub={`মোট ${stats.total_attendance ?? stats.workers}`} />
        <StatCard icon={<FaBoxOpen />} tone="amber" label="আজ মাল বেরিয়েছে" value={stats.stock_today} />
        <StatCard icon={<FaListCheck />} tone="rose" label="পেন্ডিং অনুরোধ" value={stats.pending} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Today's issues */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <SectionTitle title="আজ কে কী মাল নিলো" sub="স্টক আউট লগ — আজ" />
          <div className="space-y-2.5 max-h-80 overflow-y-auto">
            {today_issues?.map((x, i) => (
              <div key={i} className="flex items-center justify-between text-sm gap-3 bg-ink-50 rounded-lg px-3.5 py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><FaBoxOpen /></span>
                  <span className="font-medium text-ink-800 truncate">{x.material_name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-ink-900">{x.qty}</span>
                  <span className="text-[11px] text-ink-400 truncate max-w-[90px]">{x.contractor_name || 'সাধারণ'}</span>
                </div>
              </div>
            ))}
            {(!today_issues || today_issues.length === 0) && <div className="text-sm text-ink-400 py-8 text-center">আজ এখনো কোনো মাল বিতরণ হয়নি</div>}
          </div>
        </div>

        {/* Attendance per contractor */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <SectionTitle title="আজ সাইড অনুযায়ী হাজিরা" sub="কে কাজ করেছে, কে অনুপস্থিত" />
          <div className="space-y-3">
            {(attendance_by_contractor || []).map((c) => (
              <div key={c.contractor_id} className="flex items-center justify-between text-sm">
                <span className="font-medium text-ink-800 truncate max-w-[45%]">{c.contractor_name}</span>
                <div className="flex gap-1.5 shrink-0">
                  <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">{c.present ?? 0} উপস্থিত</span>
                  <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-rose-50 text-rose-700">{c.absent ?? 0} অনুপস্থিত</span>
                </div>
              </div>
            ))}
            {(!attendance_by_contractor || attendance_by_contractor.length === 0) && <div className="text-sm text-ink-400 py-8 text-center">আজ হাজিরা নেওয়া হয়নি</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Contractor ───────────────────────────────────────────────────
function ContractorDash() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/contractor/dashboard').then(({ data }) => setS(data.stats)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink-900">{greeting()}, কন্ট্রাক্টর 👋</h1>
        <p className="text-sm text-ink-500 mt-0.5">আপনার টিম ও মালের সারসংক্ষেপ</p>
      </div>
      {s ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={<FaUsers />} tone="sky" label="আমার কর্মী" value={s.workers} />
          <StatCard icon={<FaCircleCheck />} tone="emerald" label="আজ উপস্থিত" value={s.present_today} />
          <StatCard icon={<FaBoxOpen />} tone="amber" label="মাল নিয়েছি" value={s.stock_out} />
          <StatCard icon={<FaListCheck />} tone="rose" label="পেন্ডিং অনুরোধ" value={s.pending_requests} />
        </div>
      ) : <Loading />}
    </div>
  );
}

// ── Worker ───────────────────────────────────────────────────────
function WorkerDash() {
  const { profile } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/worker/dashboard').then(({ data }) => setD(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink-900">{greeting()}, {profile?.user?.name} 👋</h1>
        <p className="text-sm text-ink-500 mt-0.5">আপনার হাজিরা ও বেতনের সারসংক্ষেপ</p>
      </div>
      {d ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <StatCard icon={<FaClipboardCheck />} tone="emerald" label="মোট উপস্থিত দিন" value={d.stats?.present_days} />
          {d.last_salary ? (
            <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
              <div className="text-xs font-semibold text-ink-400 uppercase tracking-wide">সর্বশেষ বেতন</div>
              <div className="text-2xl font-bold text-ink-900 mt-1">৳{d.last_salary.net_amount}</div>
              <div className="mt-2"><Badge color={d.last_salary.status}>{d.last_salary.status}</Badge></div>
            </div>
          ) : (
            <StatCard icon={<FaSackDollar />} tone="amber" label="সর্বশেষ বেতন" value="—" />
          )}
        </div>
      ) : <Loading />}
    </div>
  );
}